import {
    AccessTime as AccessTimeOutlined,
    BarChart as BarChartOutlined,
    CalendarMonth as CalendarMonthOutlined,
    ChevronLeft,
    Dashboard as DashboardOutlined,
    DirectionsBus as DirectionsBusOutlined,
    ExpandLess,
    ExpandMore,
    LocalShipping as LocalShippingOutlined,
    LocationOn as LocationOnOutlined,
    Logout,
    Menu as MenuIcon,
    Notifications,
    Policy,
    Receipt as ReceiptOutlined,
    ReceiptLong as ReceiptLongOutlined,
    Router as RouterOutlined,
    Settings,
    SettingsOutlined,
    Work as WorkOutlined,
} from "@mui/icons-material";
import {
    Avatar,
    Box,
    Divider,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Tooltip from "@mui/material/Tooltip";
import * as React from "react";
import { memo, SyntheticEvent, useCallback, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Base } from "../../framework/base";
import { customHistory } from "../../framework/customHistory";
import { IEmployeeEdit } from "../../models/employee/employeeEdit";
import { IEmployeeParameters } from "../../models/employee/employeeIParameters";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { Session } from "../../models/session/session";
import { ISessionState } from "../../models/session/sessionState";
import * as employeeService from "../../services/employeeService";
import { OwnerSettingsDialog } from "../owner/ownerSettingsDialog";
import { OwnSettingsDialog } from "../user/ownSettingsDialog";
import { Link as ReactLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BetaLabel } from "./betaLabel";
import { WorkShiftPlanningTab } from "../workShiftPlanning/WorkShiftPlanningRouter";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { setDrawerOpen } from "../../store/appSlice";

const DRAWER_WIDTH_FULL = 240;
const DRAWER_WIDTH_MINI = 64;

interface Page {
    title: string;
    path: string;
    exact?: boolean;
    isVisible?: () => boolean;
    children?: Page[];
    target?: string;
    isBeta?: boolean;
    icon?: React.ReactElement;
    id: string;
}

export interface Props {
    setEmployeeParameters: (
        parameters: IEmployeeParameters,
        saveToDb: boolean
    ) => void;
    onLogout: () => void;
    showMenu?: boolean;
}

interface DrawerNavIconProps {
    icon?: React.ReactElement;
    isBeta?: boolean;
    isActive: boolean;
    drawerOpen: boolean;
}

const DrawerNavIcon = ({
    icon,
    isBeta,
    isActive,
    drawerOpen,
}: DrawerNavIconProps) => {
    const iconEl = (
        <ListItemIcon
            sx={{
                minWidth: drawerOpen ? 40 : "auto",
                color: isActive ? "primary.main" : "inherit",
            }}
        >
            {icon}
        </ListItemIcon>
    );
    if (isBeta && !drawerOpen) {
        return (
            <Badge
                badgeContent="&#946;"
                sx={{
                    "& .MuiBadge-badge": {
                        backgroundColor: "orange !important",
                    },
                }}
            >
                {iconEl}
            </Badge>
        );
    }
    return iconEl;
};

interface DrawerNavItemProps {
    page: Page;
    paddingLeft: number;
    location: { pathname: string };
    navigate: (e: SyntheticEvent, path: string) => void;
    t: (key: string) => string;
}

const DrawerNavItem = ({
    page,
    paddingLeft,
    location,
    navigate,
    t,
}: DrawerNavItemProps) => (
    <ListItemButton
        id={page.id}
        selected={
            !!matchPath(location.pathname, {
                path: page.path,
                exact: page.exact,
            })
        }
        onClick={(e) => navigate(e, page.path)}
        component={Link}
        href={page.path}
        sx={{ pl: paddingLeft }}
    >
        <ListItemText
            primary={t(page.title)}
            primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
        />
    </ListItemButton>
);

interface DrawerNavGroupProps {
    group: Page;
    expandedItems: Set<string>;
    toggleExpanded: (path: string) => void;
    location: { pathname: string };
    navigate: (e: SyntheticEvent, path: string) => void;
    t: (key: string) => string;
}

const DrawerNavGroup = ({
    group,
    expandedItems,
    toggleExpanded,
    location,
    navigate,
    t,
}: DrawerNavGroupProps) => {
    const visibleChildren = (group.children ?? []).filter(
        (gc) => !gc.isVisible || gc.isVisible()
    );
    const isGroupExpanded = expandedItems.has(group.path);
    const isGroupActive = visibleChildren.some(
        (gc) =>
            !!matchPath(location.pathname, {
                path: gc.path,
                exact: gc.exact,
            })
    );
    return (
        <React.Fragment>
            <ListItemButton
                id={group.id}
                selected={isGroupActive}
                onClick={() => toggleExpanded(group.path)}
                sx={{ pl: 4 }}
            >
                <ListItemText
                    primary={t(group.title)}
                    primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                />
                {isGroupExpanded ? (
                    <ExpandLess fontSize="small" />
                ) : (
                    <ExpandMore fontSize="small" />
                )}
            </ListItemButton>
            <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                    {visibleChildren.map((gc) => (
                        <DrawerNavItem
                            key={gc.path}
                            page={gc}
                            paddingLeft={6}
                            location={location}
                            navigate={navigate}
                            t={t}
                        />
                    ))}
                </List>
            </Collapse>
        </React.Fragment>
    );
};

export const Header = memo(
    ({ setEmployeeParameters, onLogout }: Props) => {
        const [sessionState, setSessionState] = useState<ISessionState>();
        const [userNames, setUserNames] = useState<string[]>([]);
        const [pages, setPages] = useState<Page[]>();
        const [hasPrivacyPolicy, setHasPrivacyPolicy] =
            useState<boolean>(false);
        const [employeeEdit, setEmployeeEdit] = useState<IEmployeeEdit>();
        const [showOwnSettingsDialog, setShowOwnSettingsDialog] =
            useState<boolean>(false);
        const [showOwnerSettingsDialog, setShowOwnerSettingsDialog] =
            useState<boolean>(false);
        const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
        const [activePage, setActivePage] = useState<Page | null>();
        const [isVisible, setIsVisible] = useState<boolean>(false);
        const drawerOpen = useAppSelector((state) => state.app.drawerOpen);
        const dispatch = useAppDispatch();
        const [expandedItems, setExpandedItems] = useState<Set<string>>(
            new Set()
        );
        const [miniMenuAnchor, setMiniMenuAnchor] = useState<{
            el: HTMLElement;
            page: Page;
        } | null>(null);
        const location = useLocation();
        const { t } = useTranslation();

        // Set state after fetching session to avoid UI popping issues
        const getInitialStateFromSessionState = () => {
            Session.getSessionState()
                .then((sessionState) => {
                    if (!sessionState) {
                        return;
                    }

                    const ownerParameters = new OwnerParameters();

                    const pages: Page[] = [
                        {
                            title: "monitor",
                            path: "/monitor",
                            isVisible: () =>
                                ownerParameters.getOptionMonitorViewEnabled(),
                            isBeta: true,
                            icon: <DashboardOutlined />,
                            id: "menu-btn-monitor",
                        },
                        {
                            title: "reporting.worktime",
                            path: "/worktimepage",
                            icon: <AccessTimeOutlined />,
                            id: "menu-btn-worktime",
                            children: [
                                {
                                    title: "reporting.worktime",
                                    path: "/worktimepage",
                                    exact: true,
                                    id: "menu-btn-worktimepage",
                                },
                                {
                                    title: "workTime.workTimeEntries",
                                    path: "/worktimepage/details",
                                    id: "menu-btn-worktime-details",
                                },
                                {
                                    title: "salaries",
                                    path: "/worktimepage/salaries",
                                    id: "menu-btn-worktime-salaries",
                                },
                                {
                                    title: "CALCULATIONSETTINGS",
                                    path: "/worktimepage/calculation",
                                    isVisible: () =>
                                        ownerParameters.getOptionIsFunctionsAreInUse(),
                                    id: "menu-btn-worktime-calculation",
                                },
                            ],
                        },
                        {
                            title: "planning",
                            path: "/work",
                            isVisible: () =>
                                ownerParameters.getOwnerHasWorkOrdersEnabled(),
                            icon: <WorkOutlined />,
                            id: "menu-btn-planning",
                        },
                        {
                            title: "workShiftPlanning.workShiftPlanning",
                            path: "/workshiftplanning",
                            icon: <CalendarMonthOutlined />,
                            id: "menu-btn-workshiftplanning",
                            children: [
                                {
                                    title: "employee.employees",
                                    path: `/workshiftplanning/${WorkShiftPlanningTab.Employee}`,
                                    id: "menu-btn-workshiftplanning-employees",
                                },
                                {
                                    title: "reporting.fleet",
                                    path: `/workshiftplanning/${WorkShiftPlanningTab.Vehicle}`,
                                    id: "menu-btn-workshiftplanning-fleet",
                                },
                                {
                                    title: "route.routes",
                                    path: `/workshiftplanning/${WorkShiftPlanningTab.Route}`,
                                    isVisible: () =>
                                        ownerParameters.getOptionRoutesEnabled(),
                                    id: "menu-btn-workshiftplanning-routes",
                                },
                            ],
                            isVisible: () =>
                                ownerParameters.getOptionWorkShiftPlanningEnabled(),
                        },
                        {
                            title: "telematics",
                            path: "/telematics",
                            isVisible: () =>
                                sessionState.ssoIntegrations.length > 0,
                            icon: <RouterOutlined />,
                            id: "menu-btn-telematics",
                        },
                        {
                            title: "fleetManagementView.topMenuTitle",
                            path: "/fleetmanagementview",
                            isVisible: () =>
                                ownerParameters.getOptionOwnerHasEasyOppTelematicsEnabled(),
                            icon: <DirectionsBusOutlined />,
                            id: "menu-btn-fleet-management",
                            children: [
                                {
                                    title: "fleetManagementView.vehicles.tabTitle",
                                    path: "/fleetmanagementview/vehicles",
                                    exact: true,
                                    id: "menu-btn-fleet-management-vehicles",
                                },
                                {
                                    title: "fleetManagementView.maintenance.tabTitle",
                                    path: "/fleetmanagementview/maintenance",
                                    exact: true,
                                    id: "menu-btn-fleet-management-maintenance",
                                },
                                {
                                    title: "fleetManagementView.vehicleCameras.tabTitle",
                                    path: "/fleetmanagementview/vehicleCameras",
                                    id: "menu-btn-fleet-management-cameras",
                                },
                            ],
                        },
                        {
                            title: "transport.orders",
                            path: "/transport/orders",
                            isVisible: () =>
                                ownerParameters.getOwnerHasTransportEnabled(),
                            isBeta: true,
                            icon: <LocalShippingOutlined />,
                            id: "menu-btn-transport-orders",
                        },
                        {
                            title: "invoicing",
                            path: "/invoicingbeta",
                            isVisible: () =>
                                ownerParameters.getOwnerHasTransportInvoicingEnabled(),
                            isBeta: true,
                            icon: <ReceiptLongOutlined />,
                            id: "menu-btn-invoicing",
                            children: [
                                {
                                    title: "transport.orders",
                                    path: "/invoicingbeta/transportorders",
                                    id: "menu-btn-invoicing-orders",
                                },
                                {
                                    title: "invoices",
                                    path: "/invoicingbeta/transportorderinvoices",
                                    id: "menu-btn-invoicing-invoices",
                                },
                            ],
                        },
                        {
                            title: "invoicing",
                            path: "/invoicing/workpage",
                            isVisible: () =>
                                ownerParameters.getOwnerHasWorkOrdersEnabled(),
                            icon: <ReceiptOutlined />,
                            id: "menu-btn-invoicing-legacy",
                        },
                        {
                            title: "reporting.reporting",
                            path: "/reporting",
                            isVisible: () =>
                                ownerParameters.getOwnerHasReportingEnabled(),
                            icon: <BarChartOutlined />,
                            id: "menu-btn-reporting",
                            children: [
                                {
                                    title: "reporting.groupableWorkTime",
                                    path: "/reporting/groupable",
                                    isVisible: () =>
                                        ownerParameters.getOptionWorkTimeGroupableReportEnabled(),
                                    id: "menu-btn-reporting-groupable",
                                },
                                {
                                    title: "reporting.fleet",
                                    path: "/reporting/vehicles",
                                    isVisible: () =>
                                        ownerParameters.getOptionVehicleReportEnabled(),
                                    id: "menu-btn-reporting-vehicles",
                                },
                                {
                                    title: "reporting.groupableFleet",
                                    path: "/reporting/groupableVehicles",
                                    isVisible: () =>
                                        ownerParameters.getOptionVehicleReportEnabled(),
                                    id: "menu-btn-reporting-groupable-vehicles",
                                },
                                {
                                    title: "reporting.formSummary.tabTitle",
                                    path: "/reporting/formsummary",
                                    exact: true,
                                    isVisible: () =>
                                        ownerParameters.getOptionFormsEnabled(),
                                    id: "menu-btn-reporting-form-summary",
                                },
                                {
                                    title: "reporting.driverLog",
                                    path: "/reporting/driverLog",
                                    isVisible: () =>
                                        ownerParameters.getOptionOwnerHasEasyOppTelematicsEnabled(),
                                    id: "menu-btn-reporting-driverLog",
                                },
                            ],
                        },

                        {
                            title: "tracking.tracking",
                            path: "/tracking",
                            icon: <LocationOnOutlined />,
                            id: "menu-btn-tracking",
                            children: [
                                {
                                    title: "tracking.eventLog",
                                    path: "/tracking/eventlog",
                                    id: "menu-btn-tracking-eventlog",
                                },
                                {
                                    title: "tracking.operationLog",
                                    path: "/tracking/operationlog",
                                    id: "menu-btn-tracking-operationlog",
                                },
                            ],
                        },
                        {
                            title: "maintenance",
                            path: "/settings",
                            icon: <SettingsOutlined />,
                            id: "menu-btn-maintenance",
                            children: [
                                {
                                    title: "settings.customers",
                                    path: "/settings/customers",
                                    id: "menu-btn-settings-customers-group",
                                    children: [
                                        {
                                            title: "settings.customerTypes",
                                            path: "/settings/customers/customertypes",
                                            id: "menu-btn-settings-customer-types",
                                        },
                                        {
                                            title: "settings.customers",
                                            path: "/settings/customers/customers",
                                            id: "menu-btn-settings-customers",
                                        },
                                        {
                                            title: "settings.customerSites",
                                            path: "/settings/customers/customersites",
                                            id: "menu-btn-settings-customer-sites",
                                        },
                                        {
                                            title: "settings.contacts",
                                            path: "/settings/customers/contacts",
                                            id: "menu-btn-settings-contacts",
                                        },
                                    ],
                                },
                                {
                                    title: "settings.products",
                                    path: "/settings/products",
                                    id: "menu-btn-settings-products-group",
                                    children: [
                                        {
                                            title: "settings.productGroups",
                                            path: "/settings/products/productgroups",
                                            id: "menu-btn-settings-product-groups",
                                        },
                                        {
                                            title: "settings.products",
                                            path: "/settings/products/products",
                                            id: "menu-btn-settings-products",
                                        },
                                    ],
                                },
                                {
                                    title: "settings.employees",
                                    path: "/settings/employees",
                                    id: "menu-btn-settings-employees-group",
                                    children: [
                                        {
                                            title: "settings.employeeGroups",
                                            path: "/settings/employees/employeegroups",
                                            id: "menu-btn-settings-employee-groups",
                                        },
                                        {
                                            title: "settings.employees",
                                            path: "/settings/employees/employees",
                                            id: "menu-btn-settings-employees",
                                        },
                                        {
                                            title: "settings.competencies",
                                            path: "/settings/employees/competencies",
                                            id: "menu-btn-settings-competencies",
                                        },
                                        {
                                            title: "settings.employeeCompetencies",
                                            path: "/settings/employees/employeecompetencies",
                                            id: "menu-btn-settings-employee-competencies",
                                        },
                                    ],
                                },
                                {
                                    title: "settings.vehicles",
                                    path: "/settings/vehicles",
                                    id: "menu-btn-settings-vehicles-group",
                                    children: [
                                        {
                                            title: "settings.vehicleGroups",
                                            path: "/settings/vehicles/vehiclegroups",
                                            id: "menu-btn-settings-vehicle-groups",
                                        },
                                        {
                                            title: "settings.vehicles",
                                            path: "/settings/vehicles/vehicles",
                                            id: "menu-btn-settings-vehicles",
                                        },
                                        {
                                            title: "settings.tools",
                                            path: "/settings/vehicles/tools",
                                            id: "menu-btn-settings-tools",
                                        },
                                    ],
                                },
                                {
                                    title: "settings.costCenters",
                                    path: "/settings/costcenters/costcenters",
                                    id: "menu-btn-settings-cost-centers",
                                },
                                {
                                    title: "settings.workTimeTypes",
                                    path: "/settings/worktimetypes/worktimetypes",
                                    id: "menu-btn-settings-work-time-types",
                                },
                                {
                                    title: "settings.orderTypes",
                                    path: "/settings/ordertypes/ordertypes",
                                    isVisible: () =>
                                        ownerParameters.getOptionCustomOrderTypesEnabled(),
                                    id: "menu-btn-settings-order-types",
                                },
                                {
                                    title: "settings.routes",
                                    path: "/settings/routes/routes",
                                    isVisible: () =>
                                        ownerParameters.getOptionRoutesEnabled(),
                                    id: "menu-btn-settings-routes",
                                },
                                {
                                    title: "settings.forms.tabTitle",
                                    path: "/settings/forms/forms",
                                    isVisible: () =>
                                        ownerParameters.getOptionFormsEnabled(),
                                    id: "menu-btn-settings-forms",
                                },
                            ],
                        },
                    ];

                    setPages(pages);
                    setSessionState(sessionState);
                    setUserNames(sessionState.fullName.trim().split(" "));
                    setHasPrivacyPolicy(!!ownerParameters.getPrivacyPolicy());
                })
                .catch((err) => console.error(err));
        };

        useEffect(() => {
            if (location.pathname === "/login") {
                setIsVisible(false);
                return;
            } else if (!isVisible) {
                getInitialStateFromSessionState();
                setIsVisible(true);
            }

            if (!pages) {
                return;
            }

            const activePage =
                pages.find(({ path, exact }) =>
                    matchPath(location.pathname, { path, exact })
                ) ?? null;

            setActivePage(activePage);
        }, [location, pages]);

        // Auto-expand the active parent's children
        useEffect(() => {
            if (activePage?.children) {
                setExpandedItems((prev) => new Set([...prev, activePage.path]));
            }
        }, [activePage?.path]);

        // Auto-expand nested groups when navigating to a grandchild path
        useEffect(() => {
            if (!pages) return;
            pages.forEach((page) => {
                page.children?.forEach((child) => {
                    if (child.children) {
                        const isChildActive = child.children.some(
                            (gc) =>
                                (!gc.isVisible || gc.isVisible()) &&
                                !!matchPath(location.pathname, {
                                    path: gc.path,
                                    exact: gc.exact,
                                })
                        );
                        if (isChildActive) {
                            setExpandedItems(
                                (prev) => new Set([...prev, child.path])
                            );
                        }
                    }
                });
            });
        }, [location.pathname, pages]);

        const handleOwnSettingsClick = () => {
            void employeeService
                .getOwnSettings()
                .then((editItem) => {
                    setEmployeeEdit(editItem);
                    setShowOwnSettingsDialog(true);
                })
                .catch(console.error)
                .finally(() => setMenuAnchorEl(null));
        };

        const handlePrivacyPolicyClick = () => {
            if (hasPrivacyPolicy) {
                window.open(new OwnerParameters().getPrivacyPolicy(), "_blank");
                setMenuAnchorEl(null);
            }
        };

        const handleLogOutClick = () => {
            setMenuAnchorEl(null);
            if (!Base.isNullOrUndefined(onLogout)) {
                onLogout();
            }
        };

        const navigate = useCallback((e: SyntheticEvent, path: string) => {
            e.preventDefault();
            customHistory.push(path);
        }, []);

        const toggleExpanded = useCallback((path: string) => {
            setExpandedItems((prev) => {
                const next = new Set(prev);
                if (next.has(path)) {
                    next.delete(path);
                } else {
                    next.add(path);
                }
                return next;
            });
        }, []);

        const handleDrawerToggle = useCallback(() => {
            dispatch(setDrawerOpen(!drawerOpen));
        }, [dispatch, drawerOpen]);

        const handleParentClick = useCallback(
            (e: React.MouseEvent<HTMLElement>, page: Page) => {
                e.preventDefault();
                if (!page.children) return;
                if (drawerOpen) {
                    customHistory.push(page.path);
                } else {
                    setMiniMenuAnchor({ el: e.currentTarget, page });
                }
            },
            [drawerOpen]
        );

        if (!isVisible) {
            return null;
        }

        const transportOrdersWithNotificationsEnabled =
            new OwnerParameters().getOwnerHasTransportEnabled() &&
            new OwnerParameters().getOwnerHasTransportNotificationsEnabled();

        const drawerSx = {
            width: drawerOpen ? DRAWER_WIDTH_FULL : DRAWER_WIDTH_MINI,
            flexShrink: 0,
            zIndex: 1001,
            whiteSpace: "nowrap" as const,
            boxSizing: "border-box" as const,
            "& .MuiDrawer-paper": {
                width: drawerOpen ? DRAWER_WIDTH_FULL : DRAWER_WIDTH_MINI,
                overflowX: "hidden",
                transition: (theme: {
                    transitions: {
                        create: (prop: string, opts: object) => string;
                        easing: { sharp: string };
                        duration: {
                            enteringScreen: number;
                            leavingScreen: number;
                        };
                    };
                }) =>
                    theme.transitions.create("width", {
                        easing: theme.transitions.easing.sharp,
                        duration: drawerOpen
                            ? theme.transitions.duration.enteringScreen
                            : theme.transitions.duration.leavingScreen,
                    }),
                display: "flex",
                flexDirection: "column",
                backgroundColor: "light.main",
            },
        };

        return (
            <>
                <Drawer variant="permanent" sx={drawerSx}>
                    {/* Logo + toggle button */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: drawerOpen
                                ? "space-between"
                                : "center",
                            px: drawerOpen ? 2 : 0,
                            py: 1,
                            minHeight: 64,
                            bgcolor: "primary.dark",
                            flexShrink: 0,
                        }}
                    >
                        {drawerOpen && (
                            <ReactLink to="/">
                                {sessionState?.ownerLogo ? (
                                    <img
                                        src={
                                            "data:image/*;base64," +
                                            sessionState.ownerLogo
                                        }
                                        alt="Decorative logo"
                                        style={{ maxHeight: 40 }}
                                    />
                                ) : (
                                    <img
                                        src={
                                            appConfig.ownRoot +
                                            "EasyOpp_nega.svg"
                                        }
                                        alt="EasyOpp - Decorative logo"
                                        style={{ height: 40 }}
                                    />
                                )}
                            </ReactLink>
                        )}
                        <IconButton
                            onClick={handleDrawerToggle}
                            sx={{ color: "primary.contrastText" }}
                            size="small"
                        >
                            {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
                        </IconButton>
                    </Box>

                    <Divider />

                    {/* Navigation items */}
                    <List
                        sx={{
                            flexGrow: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            py: 1,
                        }}
                    >
                        {pages?.map((page) => {
                            if (page.isVisible && !page.isVisible())
                                return null;
                            const isActive = activePage?.path === page.path;
                            const isExpanded = expandedItems.has(page.path);

                            if (page.children) {
                                const visibleChildren = page.children.filter(
                                    (c) => !c.isVisible || c.isVisible()
                                );
                                return (
                                    <React.Fragment key={page.path}>
                                        <Tooltip
                                            title={t(page.title)}
                                            placement="right"
                                            disableHoverListener={drawerOpen}
                                            disableFocusListener={drawerOpen}
                                            disableTouchListener={drawerOpen}
                                            arrow
                                        >
                                            <ListItemButton
                                                selected={isActive}
                                                id={page.id}
                                                component={Link}
                                                href={page.path}
                                                onClick={(
                                                    e: React.MouseEvent<HTMLElement>
                                                ) =>
                                                    handleParentClick(e, {
                                                        ...page,
                                                        children:
                                                            visibleChildren,
                                                    })
                                                }
                                                sx={{
                                                    px: drawerOpen ? 2 : "auto",
                                                    justifyContent: drawerOpen
                                                        ? "initial"
                                                        : "center",
                                                }}
                                            >
                                                <DrawerNavIcon
                                                    icon={page.icon}
                                                    isBeta={page.isBeta}
                                                    isActive={isActive}
                                                    drawerOpen={drawerOpen}
                                                />
                                                {drawerOpen && (
                                                    <ListItemText
                                                        primary={
                                                            page.isBeta ? (
                                                                <BetaLabel
                                                                    betaTitle={t(
                                                                        page.title
                                                                    )}
                                                                />
                                                            ) : (
                                                                t(page.title)
                                                            )
                                                        }
                                                        primaryTypographyProps={{
                                                            noWrap: true,
                                                            fontSize: 14,
                                                        }}
                                                    />
                                                )}
                                                {drawerOpen && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            toggleExpanded(
                                                                page.path
                                                            );
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLess fontSize="small" />
                                                        ) : (
                                                            <ExpandMore fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                )}
                                            </ListItemButton>
                                        </Tooltip>
                                        {drawerOpen && (
                                            <Collapse
                                                in={isExpanded}
                                                timeout="auto"
                                                unmountOnExit
                                            >
                                                <List disablePadding>
                                                    {visibleChildren.map(
                                                        (child) =>
                                                            child.children ? (
                                                                <DrawerNavGroup
                                                                    key={
                                                                        child.path
                                                                    }
                                                                    group={
                                                                        child
                                                                    }
                                                                    expandedItems={
                                                                        expandedItems
                                                                    }
                                                                    toggleExpanded={
                                                                        toggleExpanded
                                                                    }
                                                                    location={
                                                                        location
                                                                    }
                                                                    navigate={
                                                                        navigate
                                                                    }
                                                                    t={t}
                                                                />
                                                            ) : (
                                                                <DrawerNavItem
                                                                    key={
                                                                        child.path
                                                                    }
                                                                    page={child}
                                                                    paddingLeft={
                                                                        4
                                                                    }
                                                                    location={
                                                                        location
                                                                    }
                                                                    navigate={
                                                                        navigate
                                                                    }
                                                                    t={t}
                                                                />
                                                            )
                                                    )}
                                                </List>
                                            </Collapse>
                                        )}
                                    </React.Fragment>
                                );
                            }

                            return (
                                <Tooltip
                                    key={page.path}
                                    title={t(page.title)}
                                    placement="right"
                                    disableHoverListener={drawerOpen}
                                    disableFocusListener={drawerOpen}
                                    disableTouchListener={drawerOpen}
                                    arrow
                                >
                                    <ListItemButton
                                        selected={isActive}
                                        id={page.id}
                                        onClick={
                                            page.target
                                                ? undefined
                                                : (e) => navigate(e, page.path)
                                        }
                                        component={Link}
                                        href={page.path}
                                        target={page.target}
                                        sx={{
                                            px: drawerOpen ? 2 : "auto",
                                            justifyContent: drawerOpen
                                                ? "initial"
                                                : "center",
                                        }}
                                    >
                                        <DrawerNavIcon
                                            icon={page.icon}
                                            isBeta={page.isBeta}
                                            isActive={isActive}
                                            drawerOpen={drawerOpen}
                                        />
                                        {drawerOpen && (
                                            <ListItemText
                                                primary={
                                                    page.isBeta ? (
                                                        <BetaLabel
                                                            betaTitle={t(
                                                                page.title
                                                            )}
                                                        />
                                                    ) : (
                                                        t(page.title)
                                                    )
                                                }
                                                primaryTypographyProps={{
                                                    noWrap: true,
                                                    fontSize: 14,
                                                }}
                                            />
                                        )}
                                    </ListItemButton>
                                </Tooltip>
                            );
                        })}
                    </List>

                    {/* Mini mode children popup */}
                    <Menu
                        anchorEl={miniMenuAnchor?.el}
                        open={!!miniMenuAnchor}
                        onClose={() => setMiniMenuAnchor(null)}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                    >
                        {miniMenuAnchor?.page.children
                            ?.flatMap((child) => {
                                if (!child.isVisible || child.isVisible()) {
                                    if (child.children) {
                                        return child.children.filter(
                                            (gc) =>
                                                !gc.isVisible || gc.isVisible()
                                        );
                                    }
                                    return [child];
                                }
                                return [];
                            })
                            .sort((a, b) =>
                                t(a.title).localeCompare(t(b.title))
                            )
                            .map((item) => (
                                <MenuItem
                                    key={item.path}
                                    id={item.id}
                                    onClick={(e) => {
                                        navigate(e, item.path);
                                        setMiniMenuAnchor(null);
                                    }}
                                    selected={
                                        !!matchPath(location.pathname, {
                                            path: item.path,
                                            exact: item.exact,
                                        })
                                    }
                                >
                                    {t(item.title)}
                                </MenuItem>
                            ))}
                    </Menu>

                    <Divider />

                    {/* Bottom section: notifications + user menu */}
                    <Box sx={{ flexShrink: 0, pb: 1 }}>
                        {transportOrdersWithNotificationsEnabled && (
                            <Tooltip
                                title={t("common.notifications")}
                                placement="right"
                                disableHoverListener={drawerOpen}
                                disableFocusListener={drawerOpen}
                                disableTouchListener={drawerOpen}
                                arrow
                            >
                                <ListItemButton
                                    onClick={(e) =>
                                        navigate(e, "/notifications")
                                    }
                                    component={Link}
                                    href="/notifications"
                                    sx={{
                                        px: drawerOpen ? 2 : "auto",
                                        justifyContent: drawerOpen
                                            ? "initial"
                                            : "center",
                                    }}
                                    id="menu-btn-notifications"
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: drawerOpen ? 40 : "auto",
                                        }}
                                    >
                                        <Notifications />
                                    </ListItemIcon>
                                    {drawerOpen && (
                                        <ListItemText
                                            primary={t("common.notifications")}
                                            primaryTypographyProps={{
                                                noWrap: true,
                                                fontSize: 14,
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        )}

                        <Tooltip
                            title={`${userNames?.[0] ?? ""} ${userNames?.[1] ?? ""}`.trim()}
                            placement="right"
                            disableHoverListener={drawerOpen}
                            disableFocusListener={drawerOpen}
                            disableTouchListener={drawerOpen}
                            arrow
                        >
                            <ListItemButton
                                onClick={(e) =>
                                    !menuAnchorEl
                                        ? setMenuAnchorEl(e.currentTarget)
                                        : setMenuAnchorEl(null)
                                }
                                sx={{
                                    px: drawerOpen ? 2 : "auto",
                                    justifyContent: drawerOpen
                                        ? "initial"
                                        : "center",
                                }}
                                id="menu-btn-user"
                            >
                                <ListItemIcon
                                    sx={{ minWidth: drawerOpen ? 40 : "auto" }}
                                >
                                    <Avatar
                                        sx={{
                                            fontWeight: "bold",
                                            color: "primary.dark",
                                            bgcolor: "primary.contrastText",
                                            height: 30,
                                            width: 30,
                                            fontSize: 15,
                                        }}
                                    >
                                        {`${userNames?.[0]?.[0] ?? ""}${userNames?.[1]?.[0] ?? ""}`}
                                    </Avatar>
                                </ListItemIcon>
                                {drawerOpen && (
                                    <ListItemText
                                        primary={`${userNames?.[0] ?? ""} ${userNames?.[1] ?? ""}`}
                                        primaryTypographyProps={{
                                            noWrap: true,
                                            fontSize: 14,
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        </Tooltip>

                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={() => setMenuAnchorEl(null)}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    setShowOwnerSettingsDialog(true);
                                    setMenuAnchorEl(null);
                                }}
                                id="menu-btn-owner-settings"
                            >
                                <ListItemIcon>
                                    <Settings fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {sessionState?.ownerName}
                                </ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={handleOwnSettingsClick}
                                id="menu-btn-user-settings"
                            >
                                <ListItemIcon>
                                    <Settings fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {t("userSettings.ownSettings")}
                                </ListItemText>
                            </MenuItem>
                            {hasPrivacyPolicy && (
                                <MenuItem
                                    onClick={handlePrivacyPolicyClick}
                                    id="menu-btn-privacy-policy"
                                >
                                    <ListItemIcon>
                                        <Policy fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        {t("privacyPolicy")}
                                    </ListItemText>
                                </MenuItem>
                            )}
                            <Divider />
                            <MenuItem
                                onClick={handleLogOutClick}
                                id="menu-btn-logout"
                            >
                                <ListItemIcon>
                                    <Logout fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t("signOut")}</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Drawer>

                {showOwnSettingsDialog && (
                    <OwnSettingsDialog
                        employeeEdit={employeeEdit}
                        setEmployeeParameters={setEmployeeParameters}
                        onOk={() => setShowOwnSettingsDialog(false)}
                        onCancel={() => setShowOwnSettingsDialog(false)}
                    />
                )}
                {showOwnerSettingsDialog && (
                    <OwnerSettingsDialog
                        onCancel={() => setShowOwnerSettingsDialog(false)}
                    />
                )}
            </>
        );
    },
    () => {
        // Never re-render on prop change, because App.tsx renders constantly for no reason
        return true;
    }
);
